import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generateSequenceName, useT, useUniqNamesErrors } from '@progress-fe/core';
import { Button, VStack } from '@chakra-ui/react';
import { SimpleTable } from '../../tables';
import { IconButton } from '../../buttons';
import { useJsFieldFormData } from '../../../hooks';
import { tableDataUtils } from '../../../utils';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldInspector } from '../../jsFormBase';
const NAME_PREFIX = 'Req-';
/**
 * Component renders "/schemas/jsf-hydrotreating-diesel-reactors-table" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of THydrotreatingDieselReactorsTableFormData.
 */
const HydrotreatingDieselReactorsTableJsFieldFC = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const { t } = useT();
    const [errors, setErrors] = useState(null);
    const isReadOnly = !!props.schema.readOnly;
    const { fieldConfig, fieldValues, onChangeFieldValues } = useJsFieldFormData({
        config: props.formData?.fieldConfig || null,
        values: props.formData?.fieldValues || null,
        onChange: props.onChange
    });
    const allNames = useMemo(() => fieldValues?.map((item) => item.name) || [], [fieldValues]);
    const handleRemoveRow = useCallback((deleteId) => {
        if (!fieldValues) {
            return;
        }
        const newValues = tableDataUtils.removeValueAtId(fieldValues, deleteId);
        onChangeFieldValues(newValues);
    }, [fieldValues, onChangeFieldValues]);
    const columns = useMemo(() => [
        {
            name: t('jsfields.HydrotreatingDieselReactorsTable.name'),
            type: 'string',
            align: 'left',
            propertyName: 'name'
        },
        {
            name: t('jsfields.HydrotreatingDieselReactorsTable.shelvesNum'),
            align: 'left',
            type: 'number',
            propertyName: 'shelvesNum'
        },
        {
            name: '',
            width: '28px',
            render: (rowId) => (_jsx(IconButton, { icon: "Cross", onClick: () => handleRemoveRow(rowId) }))
        }
    ], [handleRemoveRow, t]);
    const handleAddRow = useCallback(() => {
        const newReactor = {
            name: generateSequenceName(allNames, NAME_PREFIX),
            shelvesNum: '0'
        };
        const newValue = [...(fieldValues || []), newReactor];
        onChangeFieldValues(newValue);
    }, [allNames, fieldValues, onChangeFieldValues]);
    const rows = useMemo(() => {
        return (fieldValues?.map((item, idx) => ({
            key: idx,
            value: item
        })) ?? []);
    }, [fieldValues]);
    const isEnough = fieldConfig?.maxReactorsNum && fieldValues?.length === fieldConfig?.maxReactorsNum;
    const nameErrors = useUniqNamesErrors(allNames);
    useEffect(() => {
        if (!nameErrors) {
            setErrors(null);
            return;
        }
        const currentTableErrors = Object.keys(nameErrors).reduce((acc, rowId) => ({ ...acc, [rowId]: { name: true } }), {});
        setErrors(currentTableErrors);
    }, [nameErrors]);
    const handleFieldChanges = useCallback((id, value) => {
        if (!fieldValues) {
            return;
        }
        const newValues = tableDataUtils.changeValueById(fieldValues, value, id);
        setErrors(null);
        onChangeFieldValues(newValues);
    }, [fieldValues, onChangeFieldValues]);
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(VStack, { gap: "8px", "data-testid": "HydrotreatingDieselReactorsTable-test", "data-name": props.name, pb: "2px", children: [_jsx(SimpleTable, { columns: columns, rows: rows, isReadOnly: isReadOnly, noItemsTitle: t('jsfields.HydrotreatingDieselReactorsTable.noItems'), errors: errors, onValueChange: handleFieldChanges }), "(", !isEnough && (_jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", isDisabled: isReadOnly, onClick: handleAddRow, children: t('jsfields.HydrotreatingDieselReactorsTable.add') })), ")"] }) }));
};
export const HydrotreatingDieselReactorsTableJsField = memo(HydrotreatingDieselReactorsTableJsFieldFC);
//# sourceMappingURL=HydrotreatingDieselReactorsTableJsField.js.map